import { Routes } from '@angular/router';
import { authGuard, licenseGuard, loginGuard, permissionGuard } from './core/guards';
import { Permission } from './core/services';
import { AppLayout } from './core/layouts/app-layout/app-layout';
import { AuthLayout } from './core/layouts/auth-layout/auth-layout';
import { SettingsLayout } from './core/layouts/settings-layout/settings-layout';

export const routes: Routes = [
  {
    path: 'register/:id',
    loadComponent: () =>
      import('./pages/public/register-confirm/register-confirm').then((m) => m.RegisterConfirm),
  },
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  {
    path: '',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        canActivate: [loginGuard],
        loadComponent: () => import('./pages/public/login/login').then((m) => m.Login),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./pages/public/register-request/register-request').then((m) => m.RegisterRequest),
      },
      {
        path: 'azurecallback',
        loadComponent: () =>
          import('./pages/public/azurecallback/azurecallback').then((m) => m.AzureCallback),
      },
      {
        path: 'password-reset',
        loadComponent: () =>
          import('./pages/public/password-reset-request/password-reset-request').then(
            (m) => m.PasswordResetRequest,
          ),
      },
      {
        path: 'password-reset/:id',
        loadComponent: () =>
          import('./pages/public/password-reset-confirm/password-reset-confirm').then(
            (m) => m.PasswordResetConfirm,
          ),
      },
      { path: 'reset-password', redirectTo: 'password-reset', pathMatch: 'full' },
      {
        path: 'reset-password/:id',
        loadComponent: () =>
          import('./pages/public/password-reset-confirm/password-reset-confirm').then(
            (m) => m.PasswordResetConfirm,
          ),
      },
      {
        path: 'change-email/:id',
        loadComponent: () =>
          import('./pages/public/change-email-confirm/change-email-confirm').then(
            (m) => m.ChangeEmailConfirm,
          ),
      },
    ],
  },
  {
    path: 'subscription-expired',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/management/company/subscription-expired').then(
        (m) => m.SubscriptionExpired,
      ),
  },
  {
    path: 'chatbot-simulator/:chatbotId',
    loadComponent: () =>
      import('./pages/management/modules/chatbot-simulator/chatbot-simulator').then(
        (m) => m.ChatbotSimulator,
      ),
  },
  {
    path: '',
    component: AppLayout,
    canActivate: [authGuard, licenseGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        data: { permissions: [Permission.PERM_USER_CHAT_ACCESS] },
        loadComponent: () => import('./pages/user/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'inbox',
        canActivate: [permissionGuard],
        data: { permissions: [Permission.PERM_USER_CHAT_ACCESS], title: 'Inbox' },
        loadComponent: () =>
          import('./pages/user/inbox/inbox').then((m) => m.Inbox),
      },
      {
        path: 'history',
        canActivate: [permissionGuard],
        data: { permissions: [Permission.PERM_USER_CHAT_ACCESS], title: 'Gesprekshistorie' },
        loadComponent: () =>
          import('./pages/user/history/chat-history').then((m) => m.ChatHistory),
      },
      {
        path: 'contacts',
        canActivate: [permissionGuard],
        data: { permissions: [Permission.PERM_USER_CHAT_ACCESS], title: 'Contacten' },
        loadComponent: () =>
          import('./pages/user/contacts/contacts').then((m) => m.Contacts),
      },
      {
        path: 'settings',
        component: SettingsLayout,
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'chatbots' },
          {
            path: 'chatbots',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_CHATBOT_GET] },
            loadComponent: () =>
              import('./pages/management/modules/chatbots/chatbots').then((m) => m.Chatbots),
          },
          {
            path: 'chatbots/:chatbotId',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_CHATBOT_GET] },
            loadComponent: () =>
              import('./pages/management/modules/chatbot-settings/chatbot-settings').then(
                (m) => m.ChatbotSettings,
              ),
          },
          {
            path: 'live-chat',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET], title: 'Live Chat instellingen' },
            loadComponent: () =>
              import('./pages/management/modules/live-chat-settings/live-chat-settings').then(
                (m) => m.LiveChatSettingsPage,
              ),
          },
          {
            path: 'subjects',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET], title: 'Onderwerpen' },
            loadComponent: () =>
              import('./pages/management/modules/subjects/subjects-page').then((m) => m.SubjectsPage),
          },
          {
            path: 'reports',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_REPORTING_GET], title: 'Rapportages' },
            loadComponent: () =>
              import('./pages/management/modules/reports/reports').then((m) => m.ReportsPage),
          },
          {
            path: 'general',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_COMPANY_GET, Permission.PERM_PLATFORM_TENANT_GET] },
            loadComponent: () =>
              import('./pages/management/company/generalsettings/generalsettings').then((m) => m.Generalsettings),
          },
          {
            path: 'domains',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_DOMAIN_GET] },
            loadComponent: () =>
              import('./pages/management/company/domains/domains').then((m) => m.Domains),
          },
          {
            path: 'usermanagement',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_USER_GET, Permission.PERM_TENANT_ROLE_GET, Permission.PERM_TENANT_TEAM_GET] },
            loadComponent: () =>
              import('./pages/management/company/usermanagement/usermanagement').then((m) => m.Usermanagement),
          },
          {
            path: 'subscription',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_LICENSE_GET, Permission.PERM_TENANT_BILLING_GET] },
            loadComponent: () =>
              import('./pages/management/company/subscriptions/subscriptions').then(
                (m) => m.SubscriptionsComponent,
              ),
          },
          {
            path: 'integrations',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_INTEGRATION_GET] },
            loadComponent: () =>
              import('./pages/management/company/integrations/integrations').then(
                (m) => m.Integrations,
              ),
          },
          {
            path: ':companyId/general',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_COMPANY_GET, Permission.PERM_PLATFORM_TENANT_GET] },
            loadComponent: () =>
              import('./pages/management/company/generalsettings/generalsettings').then((m) => m.Generalsettings),
          },
          {
            path: ':companyId/domains',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_DOMAIN_GET] },
            loadComponent: () =>
              import('./pages/management/company/domains/domains').then((m) => m.Domains),
          },
          {
            path: ':companyId/chatbots',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_CHATBOT_GET] },
            loadComponent: () =>
              import('./pages/management/modules/chatbots/chatbots').then((m) => m.Chatbots),
          },
          {
            path: ':companyId/chatbots/:chatbotId',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_CHATBOT_GET] },
            loadComponent: () =>
              import('./pages/management/modules/chatbot-settings/chatbot-settings').then(
                (m) => m.ChatbotSettings,
              ),
          },
          {
            path: ':companyId/live-chat',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET], title: 'Live Chat instellingen' },
            loadComponent: () =>
              import('./pages/management/modules/live-chat-settings/live-chat-settings').then(
                (m) => m.LiveChatSettingsPage,
              ),
          },
          {
            path: ':companyId/subjects',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_LIVE_CHAT_SETTINGS_GET], title: 'Onderwerpen' },
            loadComponent: () =>
              import('./pages/management/modules/subjects/subjects-page').then((m) => m.SubjectsPage),
          },
          {
            path: ':companyId/reports',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_MODULE_REPORTING_GET], title: 'Rapportages' },
            loadComponent: () =>
              import('./pages/management/modules/reports/reports').then((m) => m.ReportsPage),
          },
          {
            path: ':companyId/usermanagement',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_USER_GET, Permission.PERM_TENANT_ROLE_GET, Permission.PERM_TENANT_TEAM_GET] },
            loadComponent: () =>
              import('./pages/management/company/usermanagement/usermanagement').then((m) => m.Usermanagement),
          },
          {
            path: ':companyId/subscription',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_LICENSE_GET, Permission.PERM_PLATFORM_LICENSE_GET] },
            loadComponent: () =>
              import('./pages/management/company/subscriptions/subscriptions').then(
                (m) => m.SubscriptionsComponent,
              ),
          },
          {
            path: ':companyId/integrations',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_TENANT_INTEGRATION_GET, Permission.PERM_PLATFORM_INTEGRATION_GET] },
            loadComponent: () =>
              import('./pages/management/company/integrations/integrations').then(
                (m) => m.Integrations,
              ),
          },
        ],
      },
      {
        path: 'support',
        component: SettingsLayout,
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'tenants' },
          {
            path: 'tenants',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_TENANT_'] },
            loadComponent: () => import('./pages/admin/support/tenants/tenants').then((m) => m.Tenants),
          },
          {
            path: 'communication',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_RELEASE_NOTE_'] },
            loadComponent: () => import('./pages/admin/support/communication/communication').then((m) => m.Communication),
          },
          {
            path: 'contracts',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_CONTRACT_', 'PERM_PLATFORM_ORDER_'] },
            loadComponent: () =>
              import('./pages/admin/support/contracts/contracts').then((m) => m.Contracts),
            children: [
              {
                path: 'orders',
                loadComponent: () => import('./pages/admin/support/contracts/orders/orders').then((m) => m.Orders),
              },
              {
                path: 'quotes',
                loadComponent: () => import('./pages/admin/support/contracts/quotes/quotes').then((m) => m.Quotes),
              },
              {
                path: 'renewal',
                loadComponent: () => import('./pages/admin/support/contracts/renewal/renewal').then((m) => m.Renewal),
              },
              {
                path: 'manual',
                loadComponent: () => import('./pages/admin/support/contracts/norenewal/norenewal').then((m) => m.Norenewal),
              },
              { path: 'norenewal', redirectTo: 'manual' },
            ],
          },
          {
            path: 'surveys',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_SURVEY_'] },
            loadComponent: () => import('./pages/admin/support/survey/survey').then((m) => m.Survey),
          },
        ],
      },
      {
        path: 'system',
        component: SettingsLayout,
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'scheduledtasks' },
          {
            path: 'scheduledtasks',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_SCHEDULED_TASK_'] },
            loadComponent: () =>
              import('./pages/admin/system/scheduledtasks/scheduledtasks').then((m) => m.Scheduledtasks),
          },
          {
            path: 'integrationstore',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_INTEGRATION_'] },
            loadComponent: () =>
              import('./pages/admin/system/integrationstore/integrationstore').then((m) => m.Integrationstore),
          },
          {
            path: 'configurations',
            canActivate: [permissionGuard],
            data: { permissions: [Permission.PERM_PLATFORM_CONFIGURATION_GET] },
            loadComponent: () =>
              import('./pages/management/admin/system-configurations').then(
                (m) => m.SystemConfigurations,
              ),
          },
          {
            path: 'paymentsettings',
            canActivate: [permissionGuard],
            data: { permissionPrefixes: ['PERM_PLATFORM_LICENSE_'] },
            loadComponent: () =>
              import('./pages/admin/system/paymentsettings/paymentsettings').then((m) => m.Paymentsettings),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'dashboard' },
];
